import React from 'react';
import styled from 'styled-components';
import { useMutation } from '@apollo/client';
import { Accordion, AccordionSummary, AccordionDetails, withStyles } from '@material-ui/core';
import { CloudDownload, KeyboardArrowDown } from '@material-ui/icons';
import { downloadUrl } from '@avangard/utils/browser';
import { useEnqueueStacks } from '@avangard/utils/hooks';
import { breakpoints } from '@avangard/ui/utils';
import { black, white, gray, red } from '@avangard/ui/colors';

import { Tag } from '@modules/layout/atoms';
import { FileUploader } from '@modules/shared/organisms';
import {
    DownloadSoaDocumentTemplateMutation,
    UploadSoaDocumentMutation,
} from '@modules/students/graphql';
import { SoaDocumentEntity, SoaDocumentFileEntity } from '@modules/students/entities';
import {
    DownloadSoaDocumentTemplateMutationType,
    DownloadSoaDocumentTemplateMutationVariables,
    SoaDocumentStatus,
} from '@modules/types/graphql';
import { SoaDocumentChoose } from './soa-document-choose';
import { SoaDocumentModal } from './soa-document-modal';

import type { UploadedFile } from '@modules/shared/organisms';
import {
    UploadSoaDocumentMutationType,
    UploadSoaDocumentMutationVariables,
    SoaDocumentFileVariant,
    StudentOnlineApplicationDocumentType,
} from '@modules/types/graphql';

const addIcon = <CloudDownload style={{ fontSize: 16 }} />;
const keyboardArrowDown = <KeyboardArrowDown style={{ fontSize: 24 }} />;

type StudentOnlineApplicationDocumentProps = {
    document: SoaDocumentEntity;
};

type documentToChooseState = {
    document: SoaDocumentEntity;
    documentFile: SoaDocumentFileEntity;
};

const documentToChooseInitialState = {
    document: new SoaDocumentEntity(null),
    documentFile: new SoaDocumentFileEntity(null),
};

const StudentOnlineApplicationDocument = (props: StudentOnlineApplicationDocumentProps) => {
    const { document } = props;

    const { enqueueError } = useEnqueueStacks();

    const [documentToChoose, setDocumentToChoose] = React.useState<documentToChooseState>(
        documentToChooseInitialState,
    );

    const handleSetDocumentToChoose = (
        document: SoaDocumentEntity,
        documentFile: SoaDocumentFileEntity,
    ) => setDocumentToChoose({ document, documentFile });

    const handleResetDocumentToChoose = () => setDocumentToChoose(documentToChooseInitialState);

    const [uploadSoaDocument] = useMutation<
        UploadSoaDocumentMutationType,
        UploadSoaDocumentMutationVariables
    >(UploadSoaDocumentMutation);

    const [downloadSoaDocumentTemplate] = useMutation<
        DownloadSoaDocumentTemplateMutationType,
        DownloadSoaDocumentTemplateMutationVariables
    >(DownloadSoaDocumentTemplateMutation);

    const handleUpload =
        (type: StudentOnlineApplicationDocumentType, variant: SoaDocumentFileVariant) =>
        async (file?: UploadedFile) => {
            try {
                const fileId = file?.fileId ?? null;

                await uploadSoaDocument({
                    variables: { type, variant, fileId },
                });
            } catch (error) {
                enqueueError('При загрузке файла произошла ошибка');
            }
        };

    const description = React.useMemo(() => {
        let result: JSX.Element | string | null = document.description;

        if (document.template) {
            const handleDownload = async (event: React.MouseEvent) => {
                event.preventDefault();

                try {
                    const { data: downloadSoaDocumentTemplateData } =
                        await downloadSoaDocumentTemplate({ variables: { type: document.type } });

                    if (!downloadSoaDocumentTemplateData?.downloadSoaDocumentTemplate) {
                        return;
                    }

                    downloadUrl(downloadSoaDocumentTemplateData.downloadSoaDocumentTemplate, {
                        download: document.name,
                    });
                } catch (error) {
                    enqueueError('При скачивании шаблона произошла ошибка');
                }
            };

            result = (
                <>
                    <p>
                        <button onClick={handleDownload}>Скачайте</button>

                        {document.description}
                    </p>

                    <br />

                    <p>
                        Если в документе вы увидели ошибки в данных, то необходимо вернуться к
                        предыдущим шагам анкеты, исправить и скачать документ заново.
                    </p>
                </>
            );
        }

        return result;
    }, [document, downloadSoaDocumentTemplate]);

    const hint = React.useMemo(() => {
        return document.hint;
    }, [document]);

    const status = React.useMemo(() => {
        switch (document.status) {
            case SoaDocumentStatus.pending:
                return 'normal';

            case SoaDocumentStatus.review:
                return 'warning';

            case SoaDocumentStatus.correction:
                return 'error';

            case SoaDocumentStatus.approved:
                return 'success';
        }
    }, [document]);

    const renderedDocuments = React.useMemo(() => {
        const withAccordionFiles =
            document.type === StudentOnlineApplicationDocumentType.certificate_079_086;

        if (withAccordionFiles) {
            const uploadedDocumentFile = document.files.find(documentFile =>
                documentFile.file.exists(),
            );

            return (
                <AccordionDocuments>
                    {document.files.map((documentFile, idx) => {
                        const fileData = documentFile.file.id
                            ? { filename: documentFile.file.name, fileId: documentFile.file.id }
                            : undefined;

                        let expanded = undefined;
                        let disabled = false;

                        if (uploadedDocumentFile) {
                            if (uploadedDocumentFile.file.id === documentFile.file.id) {
                                expanded = true;
                            } else {
                                disabled = true;
                            }
                        }

                        return (
                            <StyledAccordion
                                key={idx}
                                defaultExpanded={expanded}
                                disabled={disabled}
                            >
                                <StyledAccordionSummary
                                    expandIcon={keyboardArrowDown}
                                    IconButtonProps={{ disableRipple: true }}
                                >
                                    <p>{documentFile.title}</p>
                                </StyledAccordionSummary>

                                <StyledAccordionDetails>
                                    <Header>
                                        <h4>{documentFile.title}</h4>
                                    </Header>

                                    <AccordionContent>
                                        <p>{documentFile.description}</p>

                                        {documentFile.hint ? <b>{documentFile.hint}</b> : null}
                                    </AccordionContent>

                                    <AccordionFooter>
                                        <FileUploader
                                            key={idx}
                                            hideMaxSizeHint
                                            maxSize={10}
                                            accept='.jpg,.jpeg,.pdf'
                                            disabled={!document.canUploadFiles() || disabled}
                                            label={documentFile.uploadTitle}
                                            icon={addIcon}
                                            file={fileData}
                                            onUploadFile={handleUpload(
                                                document.type,
                                                documentFile.variant,
                                            )}
                                        />

                                        {documentFile.example ? (
                                            <Example>
                                                <a
                                                    target='_blank'
                                                    rel='noopener noreferrer'
                                                    href={documentFile.example}
                                                >
                                                    Посмотреть пример
                                                </a>
                                            </Example>
                                        ) : null}
                                    </AccordionFooter>
                                </StyledAccordionDetails>
                            </StyledAccordion>
                        );
                    })}
                </AccordionDocuments>
            );
        }

        const freshDocumentFile =
            document.files.find(
                documentFile => documentToChoose.documentFile.variant === documentFile.variant,
            ) || documentToChoose.documentFile;

        const isOpenDocumentModal =
            !documentToChoose.document.isTemplate && !freshDocumentFile.isTemplate;

        return (
            <>
                <Documents>
                    {document.files.map((documentFile, idx) => {
                        const handleUploadFile = handleUpload(document.type, documentFile.variant);

                        if (documentFile.documentRefTitle) {
                            return (
                                <SoaDocumentChoose
                                    documentFile={documentFile}
                                    onOpenChooseModal={() =>
                                        handleSetDocumentToChoose(document, documentFile)
                                    }
                                />
                            );
                        }

                        return (
                            <FileUploader
                                key={idx}
                                hideMaxSizeHint
                                maxSize={10}
                                accept='.jpg,.jpeg,.pdf'
                                disabled={!document.canUploadFiles()}
                                label={documentFile.uploadTitle}
                                icon={addIcon}
                                file={documentFile.getFileData()}
                                onUploadFile={handleUploadFile}
                            />
                        );
                    })}
                </Documents>

                <SoaDocumentModal
                    document={documentToChoose.document}
                    documentFile={freshDocumentFile}
                    open={isOpenDocumentModal}
                    onClose={handleResetDocumentToChoose}
                />
            </>
        );
    }, [document, document.files, documentToChoose]);

    return (
        <Root>
            <Header>
                <h4>{document.name}</h4>
                <Tag status={status}>{document.getStatusTitle()}</Tag>
            </Header>

            <Content>
                {typeof description === 'string' ? <p>{description}</p> : description}
                {typeof hint === 'string' ? <b>{hint}</b> : hint}
            </Content>

            <Footer>
                {renderedDocuments}

                {document.example ? (
                    <Example>
                        <a target='_blank' rel='noopener noreferrer' href={document.example}>
                            Посмотреть пример
                        </a>
                    </Example>
                ) : null}
            </Footer>
        </Root>
    );
};

const StyledAccordion = withStyles(() => ({
    root: {
        boxShadow: 'none',

        '&:before': {
            content: 'none',
        },
    },
}))(Accordion);

const StyledAccordionSummary = withStyles(() => ({
    root: {
        backgroundColor: white[50],
        border: `1px solid ${gray[20]}`,
        borderRadius: 4,
        boxShadow: 'none',
    },
    content: {
        color: black[80],
        fontSize: '1.6rem',
        fontWeight: 600,
    },
}))(AccordionSummary);

const StyledAccordionDetails = withStyles(() => ({
    root: {
        flexWrap: 'wrap',
        padding: '20px 16px',
    },
}))(AccordionDetails);

const Root = styled.div``;

const Header = styled.div`
    display: flex;
    flex-wrap: wrap;
    align-items: flex-start;
    justify-content: space-between;

    h4 {
        position: relative;
        max-width: 335px;

        color: ${black[80]};
        font-size: 1.6rem;
        font-weight: 600;

        &:after {
            content: '*';
            margin-left: 4px;
            color: ${red[100]};
        }
    }

    ${breakpoints.down(600)} {
        h4 {
            margin-bottom: 8px;
        }
    }
`;

const Content = styled.div`
    max-width: 350px;

    margin-top: 8px;

    p {
        color: ${black[54]};
        font-size: 1.4rem;
    }

    b {
        display: inline-flex;

        margin-top: 20px;

        font-size: 1.4rem;
        font-weight: 600;
    }

    button {
        margin-right: 4px;

        color: ${red[100]};
        font-size: 1.4rem;
        font-family: 'Open Sans';
        border: 0;
        border-bottom: 1px solid ${red[100]};
        background: transparent;

        cursor: pointer;

        &:hover {
            border-bottom-color: transparent;
        }
    }
`;

const Footer = styled.div`
    display: flex;
    flex-wrap: wrap;
    align-items: flex-end;
    width: 100%;

    margin-top: 20px;
`;

const Documents = styled.div`
    & > *:not(:last-child) {
        margin-bottom: 8px;
    }
`;

const Example = styled.div`
    margin-left: auto;
    margin-top: 8px;

    > a {
        color: ${black[54]};
        font-size: 1.4rem;
        border-bottom: 1px solid ${black[54]};

        &:hover {
            color: ${red[100]};
            border-bottom-color: ${red[100]};
        }
    }

    ${breakpoints.down(600)} {
        width: 100%;
    }
`;

const AccordionContent = styled(Content)`
    width: 100%;
    max-width: 100%;
`;

const AccordionFooter = styled(Footer)``;

const AccordionDocuments = styled(Documents)`
    width: 100%;
`;

export { StudentOnlineApplicationDocument };
